import { useEffect, useMemo, useState } from 'react';
import { Field as ReactField, useField } from 'react-final-form';
import { getLanguage, t } from 'react-switch-lang';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faQuestion } from '@awesome.me/kit-3f20e419a7/icons/classic/solid';
import { InputGroup } from 'react-bootstrap';
import Inputmask from 'inputmask';
import Image from 'next/image';
import styles from '../styles/InputFields.module.scss';
import Button from './Button';
import DatePicker from './DatePicker';
import { events } from '../utils/Amplitude';
import { validateDate } from '../utils/Validations';
import 'react-datepicker/dist/react-datepicker.css';
import RenderMarkdown from './RenderMarkdown';

function Tooltip({ text }) {
  return (
    <div className={styles.tooltip}>
      <RenderMarkdown markdown={text} />
    </div>
  );
}

export function Label({ text, id, name, optional, formatText, showToolTip = false, toolTipText }) {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  const showTooltip = () => setTooltipVisible(true);
  const hideTooltip = () => setTooltipVisible(false);
  return (
    <label htmlFor={id} id={`${name}Lbl`}>
      {text}
      {showToolTip && (
        <span
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          onFocus={showTooltip}
          onBlur={hideTooltip}
          className={styles.toolTipContainer}
        >
          <FontAwesomeIcon
            icon={faQuestion}
            className={styles.toolTipIcon}
          />
          {isTooltipVisible && <Tooltip text={toolTipText} />}
        </span>
      )}
      {optional && <span className={styles.optionalLbl}>{`(${t('Form_Lbl_Optional')})`}</span>}
      {formatText && <p className={styles.formatLbl}>{formatText}</p>}
    </label>
  );
}

export default function CustomField({ type = 'Text', ...props }) {
  if (type === 'Hidden') return null;
  if (type === 'Date') return <DateField {...props} />;
  if (type === 'Email') return <MailCheckEmail {...props} />;
  return <TextField {...props} />;
}

export function TextField({
  id,
  name,
  label,
  placeholder = '',
  helpImage = '',
  helpImageAlt = '',
  formatText,
  initialValue = '',
  format = undefined,
  activeFormat = true, // formats on input rather than just on blur
  parse = activeFormat ? format : undefined,
  autoComplete = 'off',
  className = '',
  optional = false,
  disabled = false,
  prefixItem,
  suffixItem,
  showToolTip = false,
  toolTipText = '',
  fixAlignment = false,
  fieldClassName = '',
  inputGroupClassname = '',
  validate,
  mask = null,
  errorAboveInput = null,
  inputMode = 'text',
  showClearButton = false,
}) {
  const { input, meta } = useField(name, {
    initialValue, formatOnBlur: !!format, format, parse, validate,
  });

  const fieldState = useMemo(() => {
    if (!meta.touched) return '';
    if (meta.error) return 'is-invalid';
    return 'is-valid';
  }, [meta.touched, meta.error]);

  const inputClassNames = useMemo(() => ([
    disabled && styles.disabledField,
    fieldClassName,
    'form-control',
    fieldState,
    input.value === '' && 'is-empty',
  ].filter((v) => v).join(' ')), [disabled, fieldClassName, fieldState, input.value]);

  const isMaskNumerical = /^[\d\s]+$/.test(mask);
  useEffect(() => {
    if (!mask) return undefined;
    const maskedInputElement = document.getElementById(id);
    if (!maskedInputElement) return undefined;
    Inputmask.extendDefinitions({
      a: {
        validator: '[A-Za-z]',
        casing: 'lower',
      },
    });

    // for masks with alpha characters, we need to show spaces as placeholders for better UX.
    const maskPlaceholder = isMaskNumerical ? '' : ' ';
    Inputmask({
      mask,
      placeholder: maskPlaceholder,
      showMaskOnHover: false,
      showMaskOnFocus: true,
      greedy: false,
      onBeforeMask(value) {
        // Remove all spaces before applying the mask
        // to avoid issues with pasting values with spaces
        return value.replace(/\s/g, '');
      },
    }).mask(maskedInputElement);

    // inputmask assumes key property always exists, but Edge emits a
    // keydown event without a key property when triggering form autocomplete
    function stopKeydownEventWithoutKey(e) {
      if (!e.key) e.stopImmediatePropagation();
    }
    // the third argument is to indicate that it's for the capture phase,
    // to make sure the event listener is called before the inputmask one
    maskedInputElement.addEventListener('keydown', stopKeydownEventWithoutKey, true);
    return () => maskedInputElement.removeEventListener('keydown', stopKeydownEventWithoutKey, true);
  }, [id, mask]);

  return (
    <div className={`${className} ${styles.formField}`}>
      <Label
        text={label}
        name={name}
        optional={optional}
        id={id}
        formatText={formatText}
        showToolTip={showToolTip}
        toolTipText={toolTipText}
      />
      {fieldState === 'is-invalid' && errorAboveInput}
      <div className={`${(prefixItem || suffixItem ? 'input-group' : '')} ${fixAlignment ? styles.fixFieldAlignment : ''} ${inputGroupClassname}`}>
        { prefixItem &&
          <span className={`${styles.inputGroupText} input-group-text`}>{prefixItem}</span>}
        <input
          {...input}
          id={id}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={inputClassNames}
          type={showClearButton && input.value ? 'search' : 'text'}
          inputMode={isMaskNumerical ? 'numeric' : inputMode}
        />
        {suffixItem &&
          <span className={`${styles.inputGroupText} input-group-text`}>{suffixItem}</span>}
      </div>
      {fieldState === 'is-invalid' && (
      <span id={`${id}Error`} className={`${styles.errorTxt} invalid-feedback`}>
        <RenderMarkdown markdown={meta.error} />
      </span>
      )}
      {(fieldState === 'is-invalid' && helpImage !== '') && (
        <div className={styles.helpImageContainer}>
          <Image
            src={helpImage}
            alt={helpImageAlt}
            style={{ objectFit: 'contain', objectPosition: 'left' }}
            fill
            sizes="(max-width: 768px) 100vw, 50vw"
            height={0}
            width={0}
            unoptimized // allow any remote urls
          />
        </div>
      )}
    </div>
  );
}

export function MailCheckEmail({
  id,
  name,
  label,
  placeholder = '',
  formatText,
  initialValue = '',
  format = undefined,
  activeFormat = true, // formats on input rather than just on blur
  parse = activeFormat ? format : undefined,
  className = '',
  optional = false,
  disabled = false,
  invalidEmailSuggestion,
  setInvalidEmailSuggestion,
  setShowEmailConfirm,
  showEmailConfirm,
  change,
  fieldClassName = '',
  inputGroupClassname = '',
  validate,
  showClearButton = false,
}) {
  const { input, meta } = useField(name, {
    initialValue, formatOnBlur: !!format, format, parse, validate,
  });

  const fieldState = useMemo(() => {
    if (!meta.touched) return '';
    if (meta.error) return 'is-invalid';
    return 'is-valid';
  }, [meta.touched, meta.error]);

  const inputClassNames = useMemo(() => ([
    disabled && styles.disabledField,
    fieldClassName,
    'form-control',
    fieldState,
    input.value === '' && 'is-empty',
  ].filter((v) => v).join(' ')), [disabled, fieldClassName, fieldState, input.value]);

  return (
    <div className={`${className} ${styles.formField}`}>
      <Label
        text={label}
        name={name}
        optional={optional}
        id={id}
        formatText={formatText}
      />
      <div className={`${inputGroupClassname}`}>

        <input
          {...input}
          id={id}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          className={inputClassNames}
          inputMode="email"
          type={showClearButton && input.value ? 'search' : 'text'}
        />
      </div>
      {fieldState === 'is-invalid' && (
        (invalidEmailSuggestion && !showEmailConfirm) ? (
          <div className="invalidEmailContainer">
            <span className={`${styles.errorTxt} invalid-feedback`}>{meta.error.replace('xEmail', invalidEmailSuggestion)}</span>
            <div>
              <Button
                text={t('AccountDetails_MailCheck_ButtonYes')}
                fill="outline"
                className={styles.mailCheckButton}
                ampEvent={events.USER_CLICKED_MAILCHECK_SUGGESTION_YES_BUTTON}
                ariaLabel={t('AccountDetails_MailCheck_Yes_ButtonDescription').replace('xEmail', invalidEmailSuggestion)}
                onClick={() => {
                  setInvalidEmailSuggestion(null);
                  change('Email', invalidEmailSuggestion);
                }}
              />
              <Button
                text={t('AccountDetails_MailCheck_ButtonNo')}
                fill="outline"
                className={styles.mailCheckButton}
                ampEvent={events.USER_CLICKED_MAILCHECK_SUGGESTION_NO_BUTTON}
                ariaLabel={t('AccountDetails_MailCheck_No_ButtonDescription')}
                onClick={() => {
                  setInvalidEmailSuggestion(null);
                  setShowEmailConfirm(true);
                }}
              />
            </div>
          </div>
        ) :
          (
            <span id={`${id}Error`} className={`${styles.errorTxt} invalid-feedback`}>
              <RenderMarkdown markdown={meta.error} />
            </span>
          )
      )}
    </div>
  );
}
const formatDate = (value) => (!value ?
  '' :
  // formats to YYYY-MM-DD
  value.replace(/[^\d-]/g, '')
    .replace(/^(.{10})(.*)$/g, '$1')
    .replace(/^(\d{4})(\d{1})$/, '$1-$2')
    .replace(/^(\d{4})-(\d{2})(\d{1})$/, '$1-$2-$3')
    .replace(/^(\d{4})(\d{2})(\d{2})$/, '$1-$2-$3'));

export function parseStringToDate(dateStr) {
  if (validateDate(dateStr)) return null;

  const d = new Date();
  const ar = dateStr.split('-');
  d.setFullYear(parseInt(ar[0], 10), parseInt(ar[1], 10) - 1, parseInt(ar[2], 10));
  return d;
}

export function parseDateToString(date) {
  if (!(date instanceof Date)) return '';
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  const year = date.getFullYear();

  return [year, month, day].join('-');
}
export function DateField({
  id,
  name,
  label,
  includeTime = false,
  timeLabel,
  placeholder = t('DateField_HelpText'),
  helpText,
  formatText = t('DateField_HelpText'),
  initialValue = '',
  maxDate,
  minDate,
  format = formatDate,
  parse = format,
  className = '',
  inputGroupClassname = '',
  optional = false,
  disabled = false,
  setValue,
  showClearButton = false,
}) {
  const [isOpen, setOpen] = useState(false);
  const [stateOnMouseDown, setStateOnMouseDown] = useState(false);
  const parseStringToDateObject = parseStringToDate;
  const [selectedDate, setSelectedDate] = useState(
    parseStringToDateObject(initialValue) || maxDate || new Date()
  );

  useEffect(() => {
    if (isOpen) {
      const el = document.querySelector('.react-datepicker');
      el.setAttribute('tabIndex', -1);
      el.focus();
    }
  }, [isOpen]);

  return (
    <ReactField
      name={name}
      initialValue={initialValue}
      formatOnBlur={!!format}
      format={format}
      parse={parse}
      validate={validateDate}
    >
      {({ input, meta }) => (
        <div className={`${className} ${styles.formField}`}>
          <Label text={label} name={name} optional={optional} id={id} formatText={formatText} />
          <InputGroup className={`${inputGroupClassname}`}>
            <input
              {...input}
              onChange={(e) => {
                const d = parseStringToDateObject(e.target.value);
                if (d) setSelectedDate(d);
                input.onChange(e);
              }}
              id={id}
              name={name}
              type={showClearButton && input.value ? 'search' : 'text'}
              disabled={disabled}
              placeholder={placeholder}
              className={`form-control ${(meta.touched || '') && (meta.error ? 'is-invalid' : 'is-valid')} ${input.value === '' ? 'is-empty' : ''} ${disabled ? styles.disabledField : ''}`}
            />
            <DatePicker
              onChange={(d) => {
                setSelectedDate(d);
                setValue(name, (parseDateToString(d)));
              }}
              autoComplete="off"
              dateFormat="yyyy-MM-dd"
              showMonthDropdown
              showYearDropdown
              showTimeInput={includeTime}
              timeInputLabel={timeLabel}
              preventOpenOnFocus
              autoFocus={false}
              autoCorrect={false}
              dropdownMode="select"
              placeholderText={placeholder}
              selected={selectedDate}
              open={isOpen}
              onClickOutside={() => setOpen(false)}
              onSelect={() => setOpen(false)}
              className="form-control"
              tabIndex="-1"
              disabled
              locale={getLanguage()}
              maxDate={maxDate}
              minDate={minDate}
            />
            <Button
              className="datepickerBtn"
              disabled={disabled}
              type="button"
              title={t('DatePicker_ButtonDescription')}
              ampEvent={events.USER_CLICKED_DATEPICKER_BUTTON}
              // needed to cancel out the effects of onClickOutside
              onMouseDown={() => setStateOnMouseDown(isOpen)}
              onClick={() => {
                if (stateOnMouseDown) {
                  // if popup was open on mouse down, explicitly set isOpen to false
                  // (toggle won't work because OnClickOutside is triggered first)
                  setOpen(false);
                  setStateOnMouseDown(false);
                } else {
                  setOpen(!isOpen);
                }
              }}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
            </Button>
          </InputGroup>

          {(meta.error) && (meta.touched) && (
            <span id={`${id}Error`} className={`${styles.errorTxt} invalid-feedback`}>
              <RenderMarkdown markdown={meta.error} />
            </span>
          )}

          {helpText && <p className="helpTxt">{helpText}</p>}
        </div>
      )}
    </ReactField>
  );
}
